<script setup lang="ts">
/**
 * WhatsApp Web style media composer: one large preview, a caption per file, a strip of
 * thumbnails with "+" to add more and a send button with the file count. Files are prepared
 * (converted, previewed) as soon as they arrive; the parent uploads what is ready on send.
 */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import {
  CAPTION_MAX_LENGTH,
  formatFileSize,
  type UploadIntent,
} from "@desfecha/contracts";
import Icon from "./Icon.vue";
import IconButton from "./IconButton.vue";
import EmojiPicker from "./EmojiPicker.vue";
import Modal from "./Modal.vue";
import ImageEditor, { type ImageSession } from "./ImageEditor.vue";
import { locale, t } from "../i18n";
import { toast } from "../composables/feedback";
import {
  blankEdit,
  blankHistory,
  hasHd,
  isPristine,
} from "../composables/imageEdit";
import { exportEdit } from "../composables/imageRender";
import { useEscapeLayer } from "../composables/escape";
import {
  acceptFor,
  createPrepared,
  extensionLabel,
  fileTone,
  prepare,
  release,
  type PreparedFile,
} from "../composables/mediaPrep";
import { vTooltip } from "../directives/tooltip";

const props = defineProps<{
  /** Files that opened the composer. */
  files: File[];
  intent: UploadIntent;
}>();
const emit = defineEmits<{ close: []; send: [items: PreparedFile[]] }>();

const items = ref<PreparedFile[]>([]);
const current = ref(0);
const addInput = ref<HTMLInputElement>();
const captionInput = ref<HTMLTextAreaElement>();
const emoji = ref(false);
const dragging = ref(false);
let dragDepth = 0;
/** The image editor's work per photo, its thumbnail, and whether a tool covers the caption. */
const sessions = ref<Record<string, ImageSession>>({});
const thumbs = ref<Record<string, string>>({});
const editorBusy = ref(false);
const editor = ref<InstanceType<typeof ImageEditor>>();
const discarding = ref(false);
const sending = ref(false);

const active = computed(() => items.value[current.value] ?? null);
const ready = computed(() => items.value.filter((i) => i.status === "ready"));
const preparing = computed(() =>
  items.value.some((i) => i.status === "preparing"),
);
const refused = computed(
  () => items.value.filter((i) => i.status === "error").length,
);
const captionAllowed = computed(() =>
  Boolean(active.value?.plan.ok && active.value.plan.caption),
);
const title = computed(() => active.value?.original.name ?? "");
const subtitle = computed(() => {
  const a = active.value;
  if (!a) return "";
  if (a.pages) return t("media.pages", { count: a.pages });
  return `${formatFileSize(a.original.size, locale.value)} · ${extensionLabel(a.original.name)}`;
});

const EDITABLE = /^image\/(jpeg|png|webp)$/;
function editable(
  item: PreparedFile | null,
): item is PreparedFile & { file: File } {
  return Boolean(
    item &&
    item.status === "ready" &&
    item.kind === "image" &&
    item.file &&
    EDITABLE.test(item.file.type),
  );
}
const editing = computed(() => editable(active.value));
watch(
  () => (editable(active.value) ? active.value.key : null),
  (key) => {
    if (!key) {
      editorBusy.value = false;
      return;
    }
    sessions.value[key] ??= {
      edit: blankEdit(),
      history: blankHistory(),
      quality: "standard",
    };
  },
  { immediate: true },
);

function add(files: File[] | FileList, intent: UploadIntent = props.intent) {
  const list = Array.from(files);
  if (!list.length) return;
  const first = items.value.length;
  for (const file of list) {
    items.value.push(createPrepared(file, intent));
    void prepare(items.value[items.value.length - 1]!);
  }
  current.value = first;
  focusCaption();
}

function remove(index: number) {
  const item = items.value[index];
  if (!item) return;
  release(item);
  items.value.splice(index, 1);
  if (!items.value.length) return emit("close");
  if (current.value >= items.value.length)
    current.value = items.value.length - 1;
  else if (index < current.value) current.value -= 1;
  focusCaption();
}

function select(index: number) {
  current.value = index;
  emoji.value = false;
  focusCaption();
}

function focusCaption() {
  void nextTick(() => captionInput.value?.focus());
}

/**
 * A photo goes as edited, and at WhatsApp's standard quality (1600 pixels on the longer side)
 * unless HD was chosen; an untouched photo that already fits goes as it came.
 */
async function finalize(item: PreparedFile): Promise<void> {
  if (!editable(item)) return;
  const session = sessions.value[item.key];
  const edit = session?.edit ?? blankEdit();
  const quality = session?.quality ?? "standard";
  const big = hasHd(item.width ?? 0, item.height ?? 0);
  if (isPristine(edit) && (quality === "hd" || !big)) return;
  const out = await exportEdit(item.file, edit, quality);
  const name = item.original.name.replace(/\.[^.]+$/, "") + ".jpg";
  item.file = new File([out.blob], name, { type: "image/jpeg" });
  item.width = out.w;
  item.height = out.h;
}

/** Previews of sent files now belong to the outgoing bubbles, which revoke them later. */
const handedOff = new Set<string>();
async function send() {
  if (preparing.value || sending.value || !ready.value.length) return;
  editor.value?.finishText();
  sending.value = true;
  const outgoing = ready.value.slice();
  try {
    await Promise.all(outgoing.map(finalize));
  } catch {
    sending.value = false;
    toast(t("editor.export_failed"));
    return;
  }
  sending.value = false;
  outgoing.forEach((i) => handedOff.add(i.key));
  emit("send", outgoing);
}

function askDiscard() {
  emoji.value = false;
  discarding.value = true;
}

function captionKey(e: KeyboardEvent) {
  if (e.key === "Enter" && !e.shiftKey) {
    e.preventDefault();
    send();
  }
}

function grow(e: Event) {
  const el = e.target as HTMLTextAreaElement;
  el.style.height = "auto";
  el.style.height = Math.min(120, el.scrollHeight) + "px";
}

function pickEmoji(value: string, kind: "text" | "sticker" | "image") {
  if (kind !== "text" || !active.value || !captionAllowed.value) return;
  active.value.caption = (active.value.caption + value).slice(
    0,
    CAPTION_MAX_LENGTH,
  );
  focusCaption();
}

function onPaste(e: ClipboardEvent) {
  const files = Array.from(e.clipboardData?.files ?? []);
  if (!files.length) return;
  e.preventDefault();
  add(files);
}

function onDragEnter(e: DragEvent) {
  if (!e.dataTransfer?.types.includes("Files")) return;
  dragDepth++;
  dragging.value = true;
}
function onDragLeave() {
  dragDepth = Math.max(0, dragDepth - 1);
  if (!dragDepth) dragging.value = false;
}
function onDrop(e: DragEvent) {
  dragDepth = 0;
  dragging.value = false;
  if (e.dataTransfer?.files.length) add(e.dataTransfer.files);
}

function errorText(item: PreparedFile): string {
  const e = item.error;
  if (!e) return "";
  switch (e.code) {
    case "type_not_accepted":
      return t("media.error.type", { ext: extensionLabel(item.original.name) });
    case "too_large":
      return t("media.error.too_large", {
        max: formatFileSize(e.maxBytes ?? 0, locale.value),
      });
    case "empty":
      return t("media.error.empty");
    case "heic_browser":
      return t("media.error.heic");
    default:
      return t("media.error.conversion");
  }
}

const noteText = computed(() => {
  const a = active.value;
  if (!a?.note) return "";
  return t(("media.note." + a.note) as never);
});

useEscapeLayer(emoji, () => {
  emoji.value = false;
  focusCaption();
});
useEscapeLayer(() => !discarding.value, askDiscard);

watch(
  () => props.files,
  (files) => {
    if (files.length && !items.value.length) add(files);
  },
  { immediate: true },
);
onMounted(focusCaption);
onBeforeUnmount(() => {
  items.value.filter((i) => !handedOff.has(i.key)).forEach(release);
  Object.values(thumbs.value).forEach((u) => URL.revokeObjectURL(u));
});
</script>

<template>
  <section
    class="media-composer"
    :class="{ dragging }"
    :aria-label="t('media.composer_label')"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <ImageEditor
      v-if="editing && active && sessions[active.key]"
      ref="editor"
      :key="active.key"
      v-model="sessions[active.key]!"
      :file="active.file!"
      :name="active.original.name"
      @close="askDiscard"
      @busy="(b) => (editorBusy = b)"
      @thumb="(url) => active && (thumbs[active.key] = url)"
    />
    <header v-if="!editing" class="media-composer-head">
      <span class="media-composer-title">
        <strong class="truncate" v-tooltip="title">{{ title }}</strong>
        <small class="muted">{{ subtitle }}</small>
      </span>
      <IconButton icon="close" :label="t('common.close')" @click="askDiscard" />
    </header>

    <div v-if="!editing" class="media-stage">
      <template v-if="active">
        <div v-if="active.status === 'preparing'" class="media-stage-card">
          <span class="spinner" aria-hidden="true" />
          <span class="muted">{{ t("media.preparing") }}</span>
        </div>
        <div
          v-else-if="active.status === 'error'"
          class="media-stage-card error"
        >
          <img
            v-if="active.previewUrl"
            class="media-stage-dim"
            :src="active.previewUrl"
            alt=""
          />
          <span
            v-else
            class="file-tile large"
            :style="{ '--tone': fileTone(active.original.name) }"
            >{{ extensionLabel(active.original.name) }}</span
          >
          <strong>{{ errorText(active) }}</strong>
          <button
            type="button"
            class="wa-button secondary compact"
            @click="remove(current)"
          >
            {{ t("media.remove") }}
          </button>
        </div>
        <img
          v-else-if="
            (active.kind === 'image' || active.kind === 'sticker') &&
            active.previewUrl
          "
          class="media-stage-image"
          :class="{ sticker: active.kind === 'sticker' }"
          :src="active.previewUrl"
          :alt="active.original.name"
        />
        <video
          v-else-if="active.kind === 'video' && active.previewUrl"
          class="media-stage-video"
          :src="active.previewUrl"
          controls
          playsinline
        />
        <div
          v-else-if="active.kind === 'audio' && active.previewUrl"
          class="media-stage-card"
        >
          <span
            class="file-tile large"
            :style="{ '--tone': fileTone(active.original.name) }"
            ><Icon name="audio" :size="34"
          /></span>
          <audio :src="active.previewUrl" controls />
        </div>
        <img
          v-else-if="active.previewUrl"
          class="media-stage-page"
          :src="active.previewUrl"
          :alt="active.original.name"
        />
        <div v-else class="media-stage-card">
          <span
            class="file-tile large"
            :style="{ '--tone': fileTone(active.original.name) }"
            >{{ extensionLabel(active.original.name) }}</span
          >
          <strong>{{ t("media.no_preview") }}</strong>
          <span class="muted"
            >{{ formatFileSize(active.original.size, locale) }} ·
            {{ extensionLabel(active.original.name) }}</span
          >
        </div>
        <span v-if="noteText && active.status === 'ready'" class="media-note"
          ><Icon name="info" :size="14" />{{ noteText }}</span
        >
      </template>
      <div v-if="dragging" class="media-drop-hint">
        <Icon name="plus" :size="28" />{{ t("media.drop_more") }}
      </div>
    </div>

    <div v-show="!(editing && editorBusy)" class="media-caption">
      <Transition name="pop"
        ><EmojiPicker
          v-if="emoji"
          class="media-emoji"
          @choose="pickEmoji"
          @close="emoji = false"
      /></Transition>
      <div class="media-caption-pill" :class="{ disabled: !captionAllowed }">
        <textarea
          v-if="active"
          ref="captionInput"
          v-model="active.caption"
          rows="1"
          :maxlength="CAPTION_MAX_LENGTH"
          :disabled="!captionAllowed"
          :placeholder="
            active.status === 'error'
              ? t('media.caption_refused')
              : captionAllowed
                ? t('media.caption')
                : t('media.caption_none')
          "
          :aria-label="t('media.caption')"
          @keydown="captionKey"
          @input="grow"
          @paste="onPaste"
        />
        <small
          v-if="active && active.caption.length > CAPTION_MAX_LENGTH - 100"
          class="media-caption-count"
          >{{ active.caption.length }}/{{ CAPTION_MAX_LENGTH }}</small
        >
        <IconButton
          icon="smile"
          :label="t('media.emoji')"
          :active="emoji"
          :disabled="!captionAllowed"
          @click="emoji = !emoji"
        />
      </div>
    </div>

    <footer class="media-strip-bar">
      <div class="media-strip" role="listbox" :aria-label="t('media.files')">
        <div
          v-for="(item, i) in items"
          :key="item.key"
          class="media-thumb"
          :class="{ active: i === current, error: item.status === 'error' }"
          role="option"
          :aria-selected="i === current"
          :aria-label="item.original.name"
          tabindex="0"
          @click="select(i)"
          @keydown.enter="select(i)"
          @keydown.delete="remove(i)"
        >
          <img
            v-if="thumbs[item.key] ?? item.thumbUrl"
            :src="thumbs[item.key] ?? item.thumbUrl ?? undefined"
            alt=""
          />
          <span
            v-else
            class="file-tile"
            :style="{ '--tone': fileTone(item.original.name) }"
            >{{ extensionLabel(item.original.name) }}</span
          >
          <span v-if="item.status === 'preparing'" class="media-thumb-busy"
            ><span class="spinner small"
          /></span>
          <span v-if="item.status === 'error'" class="media-thumb-alert"
            ><Icon name="xCircle" :size="14"
          /></span>
          <button
            type="button"
            class="media-thumb-remove"
            :aria-label="t('media.remove_file', { name: item.original.name })"
            @click.stop="remove(i)"
          >
            <Icon name="close" :size="12" />
          </button>
        </div>
        <button
          type="button"
          class="media-thumb add"
          :aria-label="t('media.add_more')"
          @click="addInput?.click()"
        >
          <Icon name="plus" :size="22" />
        </button>
      </div>
      <span v-if="refused" class="media-refused small">{{
        t("media.refused_count", { count: refused })
      }}</span>
      <button
        type="button"
        class="media-send"
        :disabled="preparing || sending || !ready.length"
        :aria-label="t('media.send', { count: ready.length })"
        @click="send"
      >
        <span
          v-if="preparing || sending"
          class="spinner light"
          aria-hidden="true"
        />
        <Icon v-else name="send" :size="26" />
        <span v-if="ready.length" class="media-send-count">{{
          ready.length
        }}</span>
      </button>
      <input
        ref="addInput"
        hidden
        type="file"
        multiple
        :accept="acceptFor(intent)"
        @change="
          (e: Event) => {
            const el = e.target as HTMLInputElement;
            if (el.files) add(el.files);
            el.value = '';
          }
        "
      />
    </footer>
    <Modal
      alert
      :open="discarding"
      :title="t('media.discard_title')"
      @close="discarding = false"
    >
      <template #footer>
        <button
          type="button"
          class="wa-button danger"
          @click="
            discarding = false;
            emit('close');
          "
        >
          {{ t("media.discard") }}
        </button>
        <button
          type="button"
          class="wa-button ghost"
          @click="discarding = false"
        >
          {{ t("common.cancel") }}
        </button>
      </template>
    </Modal>
  </section>
</template>
